import { CheckIcon } from '@heroicons/react/16/solid';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';

import { FileAttachmentInput } from 'src/shared/components';
import { Question } from 'src/shared/types/question';

import MarkdownEditor from 'src/shared/components/MarkdownEditor';
import { addToast, useDisclosure } from 'src/shared/components/ui';
import {
  ButtonV2,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Typography
} from 'src/shared/components/ui';

import { useApprovalQuestionForm } from './hooks/useApprovalQuestion';
import { useQuestionFiles } from './hooks/useQuestionFiles';
import { useSendQuestion } from './hooks/useSendQuestion';

interface ApprovalQuestionProps {
  question: Question;
  productId: string;
  questionId: number;
  productStage: string;
  isComplete: boolean;
  [key: string]: any;
}
const ApprovalQuestion: React.FC<ApprovalQuestionProps> = ({
  productId,
  questionId,
  productStage
}) => {
  const { isOpen, onOpenChange } = useDisclosure();
  const [approved, setApproved] = useState(false);
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useApprovalQuestionForm({
    comment: '',
    files: []
  });

  const files = watch('files');
  const comment = watch('comment');

  const questionMutation = useSendQuestion();
  const questionFilesMutation = useQuestionFiles();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    fields: fileFields,
    append: appendFile,
    remove: removeFile
  } = useFieldArray({
    control,
    name: 'files'
  });

  const isPending =
    questionMutation.isPending || questionFilesMutation.isPending;

  const onSubmitFiles = () => {
    const filesBody = new FormData();

    files.forEach((file, i) => {
      filesBody.append(`files[${i}]`, file?.file);
      filesBody.append(`captions[${i}]`, file?.caption);
    });

    questionFilesMutation.mutate(
      { body: filesBody, productId, questionId, stageId: productStage },
      {
        onSuccess: () => {
          onOpenChange();
          addToast({
            color: 'success',
            title: 'Question has been successfully answered.'
          });
        }
      }
    );
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        comment: '',
        files: []
      });
    }
  }, [isOpen, reset]);

  const onSubmit = (data: any) => {
    const withFiles = data.files.length > 0;

    const status = approved ? 'approved' : 'rejected';
    const answers = [status, data.comment];

    questionMutation.mutate(
      {
        answer: answers,
        productId,
        questionId,
        stageId: productStage,
        withFiles
      },
      {
        onSuccess: () => {
          if (withFiles) {
            onSubmitFiles();
          } else {
            onOpenChange();
            addToast({
              color: 'success',
              title: 'Question has been successfully answered.'
            });
          }
        }
      }
    );
  };

  const renderFiles = () => {
    return fileFields.map((field, index) => {
      const file = watch(`files.${index}.file`);
      const caption = watch(`files.${index}.caption`);
      const preview = watch(`files.${index}.preview`);

      return (
        <FileAttachmentInput
          key={field.id}
          fileInputProps={register(`files.${index}.file`)}
          captionInputProps={register(`files.${index}.caption`)}
          fileValue={file}
          captionValue={caption}
          preview={preview}
          onRemove={() => removeFile(index)}
        />
      );
    });
  };

  return (
    <div className="flex gap-2 mt-1 flex-col">
      <div className="flex gap-2 items-center">
        <ButtonV2
          className="min-w-32"
          onPress={() => {
            setApproved(true);
            onOpenChange();
          }}
          size="md"
          startContent={<CheckIcon className="size-5" />}
        >
          Approve
        </ButtonV2>
        <ButtonV2
          className="min-w-32"
          onPress={() => {
            setApproved(false);
            onOpenChange();
          }}
          size="md"
          color="tertiary"
          startContent={<ChatBubbleBottomCenterTextIcon className="size-5" />}
        >
          Reject
        </ButtonV2>
      </div>
      <Modal
        className="overflow-x-hidden"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 p-5 border-b border-gray-4">
                {approved ? 'Feedback' : 'Reason for rejection'}
                <Typography
                  variant="body-1"
                  weight="medium"
                  className="text-secondary-text"
                >
                  {approved
                    ? 'Your feedback is important to us'
                    : 'The supplier will reply with a new approval request'}
                </Typography>
              </ModalHeader>
              <ModalBody className="p-5">
                <form
                  className="flex flex-col gap-5"
                  id={`approval-question-form-${questionId}`}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="flex flex-col gap-1">
                    <Typography variant="body-2" weight="medium">
                      Comment
                    </Typography>
                    <Controller
                      name="comment"
                      control={control}
                      render={({ field }) => (
                        <MarkdownEditor
                          markdown={field.value}
                          onChange={field.onChange}
                          className="border rounded-xl border-gray-4"
                          contentEditableClassName="min-h-20 px-3 py-2"
                        />
                      )}
                    />
                    {errors.comment?.message && (
                      <Typography variant="body-2" className="text-danger">
                        {errors.comment.message}
                      </Typography>
                    )}
                  </div>
                </form>

                <div className="flex flex-wrap gap-2">{renderFiles()}</div>

                <div className="flex items-center gap-2 flex-grow">
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = e.target.files;

                      if (files && files.length > 0) {
                        const newEntries = Array.from(files).map((file) => ({
                          file,
                          caption: '',
                          preview: file.type.startsWith('image/')
                            ? URL.createObjectURL(file)
                            : null
                        }));

                        appendFile(newEntries);
                        e.target.value = '';
                      }
                    }}
                  />
                  <ButtonV2
                    isDisabled={isPending}
                    size="md"
                    isIconOnly
                    color="tertiary"
                    onPress={() => fileInputRef.current?.click()}
                  >
                    <PaperClipIcon className="size-4" />
                  </ButtonV2>
                </div>
              </ModalBody>
              <ModalFooter className="justify-start border-t border-gray-4 p-5">
                <ButtonV2
                  form={`approval-question-form-${questionId}`}
                  type="submit"
                  color="primary"
                  isLoading={isPending}
                  isDisabled={!comment || isPending}
                >
                  Send
                </ButtonV2>
                <ButtonV2
                  color="default"
                  variant="flat"
                  onPress={() => onClose()}
                  isDisabled={isPending}
                >
                  Close
                </ButtonV2>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ApprovalQuestion;
